import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  Checkbox,
  Container,
  CssBaseline,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { login } from "../api/authentication";
import { signup } from "../api/profile";
import { Context } from "../ContextWrapper";
import { muiTheme } from "./welcomeSetup/theme/theme";

import { Check, Close, InfoOutlined } from "@mui/icons-material";

export const WelcomeForm = () => {
  const context = useContext(Context);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const campaign = searchParams.get("campaign") || "";

  // Tab state: "login" or "signup"
  const [tab, setTab] = useState(
    location.pathname === "/signup" || campaign ? "signup" : "login"
  );

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);

    const len = digits.length;
    if (len < 4) return digits;
    if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  // ---------------- Login State ----------------
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
    showPassword: false,
    keepMeSignedIn: false,
    loginLoading: false,
  });

  // ---------------- Signup State ----------------
  const [signupData, setSignupData] = useState({
    name: "",
    surname: "",
    email: "",
    phone_number: "",
    password: "",
    confirmPassword: "",
  });

  const [isPassValid, setIsPassValid] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // --- Dynamic dashTitle centering and scaling (kept if you want right panel metrics) ---
  const [dashTitleSize, setDashTitleSize] = useState({
    width: 900,
    height: undefined,
  });
  const rightPanelRef = useRef(null);
  const dashTitleRef = useRef(null);
  const metricsRef = useRef(null);
  const [dashTitleTop, setDashTitleTop] = useState(undefined);
  const [metricsLoaded, setMetricsLoaded] = useState(false);

  const leftPanelRef = useRef(null);

  const centerDashTitle = useCallback(() => {
    if (!rightPanelRef.current || !dashTitleRef.current || !metricsRef.current)
      return;
    const panelRect = rightPanelRef.current.getBoundingClientRect();
    const dashTitleRect = dashTitleRef.current.getBoundingClientRect();
    const metricsRect = metricsRef.current.getBoundingClientRect();
    const availableSpace = metricsRect.top - panelRect.top;
    const dashTitleHeight = dashTitleRect.height;
    const top = (availableSpace - dashTitleHeight) / 2;
    setDashTitleTop(top);
  }, []);

  useEffect(() => {
    if (metricsLoaded) {
      centerDashTitle();
      window.addEventListener("resize", centerDashTitle);
      return () => window.removeEventListener("resize", centerDashTitle);
    }
  }, [metricsLoaded, centerDashTitle]);

  useEffect(() => {
    function updateDashTitleSize() {
      if (!rightPanelRef.current) return;
      const panelRect = rightPanelRef.current.getBoundingClientRect();
      const marginX = panelRect.width * 0.08;
      const marginY = panelRect.height * 0.08;
      const availableWidth = panelRect.width - 2 * marginX;
      const availableHeight = panelRect.height - 2 * marginY;
      const idealWidth = 900;
      const idealHeight = 0.25 * panelRect.height;
      let width = Math.min(idealWidth, availableWidth);
      let height = idealHeight;
      if (width < idealWidth || availableHeight < idealHeight) {
        const widthRatio = availableWidth / idealWidth;
        const heightRatio = availableHeight / idealHeight;
        const scale = Math.min(widthRatio, heightRatio, 1);
        width = idealWidth * scale;
        height = idealHeight * scale;
      }
      setDashTitleSize({ width, height });
    }
    updateDashTitleSize();
    window.addEventListener("resize", updateDashTitleSize);
    return () => window.removeEventListener("resize", updateDashTitleSize);
  }, []);

  // ---------------- Helpers & validation ----------------
  const handleLoginInputChange = (field) => (e) => {
    setLoginData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleEmailBlur =
    (field, isLogin = false) =>
    () => {
      const setter = isLogin ? setLoginData : setSignupData;
      setter((prev) => ({ ...prev, [field]: prev[field].toLowerCase() }));
    };

  const handleEmailKeyDown =
    (field, isLogin = false) =>
    (e) => {
      if (e.key === "Enter") {
        const setter = isLogin ? setLoginData : setSignupData;
        setter((prev) => ({ ...prev, [field]: prev[field].toLowerCase() }));
      }
    };

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;

    if (field === "phone_number") {
      const raw = value.replace(/\D/g, "").slice(0, 10);
      setSignupData((prev) => ({
        ...prev,
        phone_number: raw,
        phone_display: formatPhone(raw),
      }));
    } else {
      setSignupData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const getPasswordRequirements = (password, confirmPassword) => {
    const hasMinLength = password.length >= 8;
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasCapital = /[A-Z]/.test(password);
    const passwordsMatch = password === confirmPassword && password.length > 0;

    const isValid =
      hasMinLength &&
      hasSpecialChar &&
      hasNumber &&
      hasCapital &&
      passwordsMatch;
    return {
      hasMinLength,
      hasSpecialChar,
      hasNumber,
      hasCapital,
      passwordsMatch,
      isValid,
    };
  };

  const checkPasswordRequirements = useCallback((password, confirmPassword) => {
    const req = getPasswordRequirements(password, confirmPassword);
    setIsPassValid(req.isValid);
    return req;
  }, []);

  useEffect(() => {
    if (signupData.password || signupData.confirmPassword) {
      checkPasswordRequirements(
        signupData.password,
        signupData.confirmPassword
      );
    }
  }, [
    signupData.password,
    signupData.confirmPassword,
    checkPasswordRequirements,
  ]);

  // Handle autofill detection for password tooltip / positioning
  useEffect(() => {
    const handleAutofill = () => {
      if (signupData.password.length > 0) {
        setTimeout(() => {
          setSignupData((prev) => ({ ...prev, password: prev.password }));
        }, 100);
      }
    };

    const passwordInput = document.querySelector(
      'input[placeholder="Create password"]'
    );
    if (passwordInput) {
      passwordInput.addEventListener("input", handleAutofill);
      passwordInput.addEventListener("focus", handleAutofill);
    }
    return () => {
      if (passwordInput) {
        passwordInput.removeEventListener("input", handleAutofill);
        passwordInput.removeEventListener("focus", handleAutofill);
      }
    };
  }, [signupData.password]);

  // ---------------- LOGIN ----------------
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginData((prev) => ({ ...prev, loginLoading: true }));
    console.log("initiated");

    const user = {
      username: loginData.email.toLowerCase(),
      password: loginData.password,
    };

    try {
      const res = await login(user);

      if (res.status === 200) {
        // clear storage and set tokens
        localStorage.clear();
        sessionStorage.clear();

        if (loginData.keepMeSignedIn) {
          localStorage.setItem("access_token", res.data.access);
          localStorage.setItem("refresh_token", res.data.refresh);
        } else {
          sessionStorage.setItem("access_token", res.data.access);
          sessionStorage.setItem("refresh_token", res.data.refresh);
        }

        axios.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${res.data.access}`;
        window.location.href = "/";
      }
    } catch (error) {
      context.fireToast("Error", "Invalid credentials.", "error");
    } finally {
      setLoginData((prev) => ({ ...prev, loginLoading: false }));
    }
  };

  // ---------------- SIGNUP ----------------
  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupLoading(true);

    const phoneRegex = /^\+?\d{1,3}\d{9,15}$/;
    if (!phoneRegex.test(signupData.phone_number)) {
      context.fireToast("Error", "Please enter a valid phone number.", "error");
      setSignupLoading(false);
      return;
    }

    const req = checkPasswordRequirements(
      signupData.password,
      signupData.confirmPassword
    );
    if (!req.isValid) {
      context.fireToast(
        "Error",
        "Please ensure all password requirements are met.",
        "error"
      );
      setSignupLoading(false);
      return;
    }

    try {
      await signup({
        name: signupData.name,
        surname: signupData.surname,
        email: signupData.email.toLowerCase(),
        phone_number: signupData.phone_number,
        password: signupData.password,
        ...(campaign && { campaign }),
      });

      setSignupSuccess(true);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.email?.[0] ||
        err?.response?.data?.phone_number?.[0] ||
        err?.response?.data?.details ||
        "Failed to create account. Please try again.";
      context.fireToast("Error", msg, "error");
    } finally {
      setSignupLoading(false);
    }
  };
  const passwordReqs = useMemo(
    () =>
      getPasswordRequirements(signupData.password, signupData.confirmPassword),
    [signupData.password, signupData.confirmPassword]
  );

  // ---------------- Render UI (MUI) ----------------
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Box sx={{ width: "100%", height: "100vh", display: "flex" }}>
        {/* LEFT SIDE BACKGROUND */}
        <Box
          ref={leftPanelRef}
            sx={{
              flex: 1,
              display: { xs: "none", md: "block" },
              backgroundColor: "#0D1321",
              backgroundImage: "url(/reportFrontPage.png)",
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              minHeight: "100vh",
              position: "relative",
              overflow: "hidden",

              "@keyframes loginFloat1": {
                "0%":   { transform: "translate3d(0px,  0px, 0)" },
                "50%":  { transform: "translate3d(0px, -12px, 0)" },
                "100%": { transform: "translate3d(0px,  0px, 0)" },
              },
              "@keyframes loginFloat2": {
                "0%":   { transform: "translate3d(0px,  0px, 0)" },
                "50%":  { transform: "translate3d(0px,  10px, 0)" },
                "100%": { transform: "translate3d(0px,  0px, 0)" },
              },
              "@keyframes loginFloat3": {
                "0%":   { transform: "translate3d( 0px, 0px, 0)" },
                "50%":  { transform: "translate3d(-10px, -8px, 0)" },
                "100%": { transform: "translate3d( 0px, 0px, 0)" },
              },
              "@keyframes loginFloat4": {
                "0%":   { transform: "translate3d(0px,  0px, 0)" },
                "50%":  { transform: "translate3d(8px, 10px, 0)" },
                "100%": { transform: "translate3d(0px,  0px, 0)" },
              },
            }}
          >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                // Give the artwork some breathing room while still
                // expanding with the left panel (50% of viewport).
                px: { md: 3, lg: 5, xl: 7 },
                py: { md: 2, lg: 3, xl: 4 },
              }}
            >
              <Box
                component="img"
                src="/login_monthly_breakdown.svg"
                alt="Monthly Breakdown"
                sx={{
                  position: "absolute",
                  top: "6.5%",
                  right: { md: "12%", lg: "16%", xl: "20%" },
                  left: "auto",
                  width: { md: "44%", lg: "42%", xl: "40%" },
                  maxWidth: "100%",
                  height: "auto",
                  filter: "drop-shadow(0px 0px 20px rgba(76,99,215,0.85))",

                  animation: "loginFloat1 6s ease-in-out infinite",
                  animationDelay: "0s",
                  willChange: "transform",
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                }}
              />

              <Box
                component="img"
                src="/login_cash_on_hand.svg"
                alt="Cash on hand"
                sx={{
                  position: "absolute",
                  top: "25%",
                  left: { md: "6%", lg: "4%", xl: "3%" },
                  width: { md: "30%", lg: "28%", xl: "26%" },
                  height: "auto",
                  filter: "drop-shadow(0px 0px 20px rgba(76,99,215,0.85))",

                  animation: "loginFloat2 7.5s ease-in-out infinite",
                  animationDelay: "-2s",
                  willChange: "transform",
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                }}
              />

              <Box
                component="img"
                src="/login-category-breakdown.svg"
                alt="Category breakdown"
                sx={{
                  position: "absolute",
                  top: "36%",
                  right: { md: "2%", lg: "4%", xl: "6%" },
                  left: "auto",
                  width: { md: "43%", lg: "40%", xl: "38%" },
                  maxWidth: "100%",
                  height: "auto",
                  filter: "drop-shadow(0px 0px 20px rgba(76,99,215,0.85))",

                  animation: "loginFloat3 8.5s ease-in-out infinite",
                  animationDelay: "-4s",
                  willChange: "transform",
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                }}
              />

              <Box
                component="img"
                src="/login-financial-trend.svg"
                alt="Financial trend"
                sx={{
                  position: "absolute",
                  top: "70%",
                  left: { md: "15%", lg: "19%", xl: "25%" },
                  transform: "translateX(-50%)",
                  width: { md: "55%", lg: "50%", xl: "45%" },
                  height: "auto",
                  filter: "drop-shadow(0px 0px 20px rgba(76,99,215,0.85))",

                  animation: "loginFloat4 7s ease-in-out infinite",
                  animationDelay: "-1.5s",
                  willChange: "transform",
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                }}
              />
            </Box>
          </Box>
          </Box>

          {/* RIGHT SIDE FORM */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              bgcolor: "#FFFFFF",
              minHeight: "100vh",
              py: { xs: 4, sm: 6 },
              px: "20px",
              overflowY: "auto",
            }}
          >
          {signupSuccess ? (
            <Stack
              spacing={4}
              sx={{
                p: "20px",
                width: "100%",
                maxWidth: "550px",
                backgroundColor: "#FFFFFF",
                boxShadow: "0px 8px 16px 0px #00000026",
                borderRadius: "5px",
                alignItems: "center",
                my: "auto",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box
                  component="img"
                  src="/lucro-logo-light.png"
                  alt="Lucro Logo"
                  sx={{ width: "221px", height: "auto" }}
                />
              </Box>

              <Typography
                fontWeight={500}
                fontSize="24px"
                color="#234E94"
                textAlign="center"
              >
                Check Your Email
              </Typography>

              <Typography fontSize="16px" color="#666666" textAlign="center">
                We've sent you an activation link at{" "}
                <strong>{signupData.email}</strong>. Please check your email and
                click the link to complete your registration.
              </Typography>

              <FormControl fullWidth>
                <FormHelperText
                  sx={{
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#666666",
                  }}
                >
                  Didn't receive the email? Check your spam folder or try again
                  with a different email address.
                </FormHelperText>
              </FormControl>

              <Button
                fullWidth
                variant="outlined"
                sx={{
                  mt: 3,
                  borderRadius: "4px",
                  borderColor: "#536DEC",
                  color: "#536DEC",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#536DEC",
                    color: "#ffffff",
                    borderColor: "#536DEC",
                  },
                }}
                onClick={() => setSignupSuccess(false)}
              >
                Back to Sign Up
              </Button>
            </Stack>
          ) : (
            <Container
              sx={{
                p: "0px !important",
                mt: "auto",
                mb: "auto",
                maxWidth: "540px !important",
              }}
            >
              <Typography
                fontSize={22}
                color="#718096"
                textAlign="center"
                fontWeight={500}
                sx={{ fontFamily: "Rethink Sans" }}
              >
                Welcome to
              </Typography>

              <Box
                component="img"
                src="/lucro-logo-light.png"
                alt="Lucro Logo"
                sx={{
                  display: "block",
                  mx: "auto",
                  mb: 3,
                  width: "185px",
                  height: "auto",
                }}
              />

              {/* Auth Tabs */}
              <Box
                sx={{
                  display: "flex",
                  bgcolor: "#EFF2F5",
                  borderRadius: 50,
                  p: "6px 10px",
                  mb: 3,
                }}
              >
                <Button
                  fullWidth
                  onClick={() => setTab("login")}
                  sx={{
                    fontFamily: "Rethink Sans",
                    borderRadius: 50,
                    textTransform: "none",
                    bgcolor: tab === "login" ? "#FFFFFF" : "transparent",
                    color: "#262A2C",
                    fontWeight: 600,
                  }}
                >
                  Log In
                </Button>
                <Button
                  fullWidth
                  onClick={() => setTab("signup")}
                  sx={{
                    fontFamily: "Rethink Sans",
                    borderRadius: 50,
                    textTransform: "none",
                    bgcolor: tab === "signup" ? "#FFFFFF" : "transparent",
                    color: "#262A2C",
                    fontWeight: 600,
                  }}
                >
                  Create Account
                </Button>
              </Box>

              {/* Sign up */}
              {tab === "signup" && (
                <Box component="form" autoComplete="on" onSubmit={handleSignup}>
                  <Box sx={{ width: "100%" }}>
                    <Grid container spacing={2} mt="24px">
                      <Grid item size={{ xs: 12, sm: 6 }}>
                        <Typography
                          variant="subtitle2"
                          mb={1}
                          sx={{ fontFamily: "Rethink Sans" }}
                        >
                          First Name
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="John"
                          name="given-name"
                          autoComplete="given-name"
                          value={signupData.name}
                          onChange={handleInputChange("name")}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "40px",
                              borderRadius: "8px",
                              "& fieldset": { borderColor: "#E0E0E0" },
                              "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                              },
                            },
                            "& input": {
                              padding: "8px 12px",
                              fontSize: "14px",
                            },
                          }}
                        />
                      </Grid>

                      <Grid item size={{ xs: 12, sm: 6 }}>
                        <Typography
                          variant="subtitle2"
                          mb={1}
                          sx={{ fontFamily: "Rethink Sans" }}
                        >
                          Last Name
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Smith"
                          name="family-name"
                          autoComplete="family-name"
                          value={signupData.surname}
                          onChange={handleInputChange("surname")}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "40px",
                              borderRadius: "8px",
                              "& fieldset": { borderColor: "#E0E0E0" },
                              "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                              },
                            },
                            "& input": {
                              padding: "8px 12px",
                              fontSize: "14px",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={2} mt="24px">
                      <Grid item size={{ xs: 12, sm: 6 }}>
                        <Typography
                          variant="subtitle2"
                          mb={1}
                          sx={{ fontFamily: "Rethink Sans" }}
                        >
                          Email Address
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="example@gmail.com"
                          name="email"
                          autoComplete="email"
                          type="email"
                          value={signupData.email}
                          onBlur={handleEmailBlur("email")}
                          onKeyDown={handleEmailKeyDown("email")}
                          onChange={handleInputChange("email")}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "40px",
                              borderRadius: "8px",
                              "& fieldset": { borderColor: "#E0E0E0" },
                              "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                              },
                            },
                            "& input": {
                              padding: "8px 12px",
                              fontSize: "14px",
                            },
                          }}
                        />
                      </Grid>

                      <Grid item size={{ xs: 12, sm: 6 }}>
                        <Typography
                          variant="subtitle2"
                          mb={1}
                          sx={{ fontFamily: "Rethink Sans" }}
                        >
                          Phone Number
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="(555) 123-4567"
                          name="tel"
                          autoComplete="tel"
                          type="tel"
                          value={signupData.phone_display || ""}
                          onChange={handleInputChange("phone_number")}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "40px",
                              borderRadius: "8px",
                              "& fieldset": { borderColor: "#E0E0E0" },
                              "&.Mui-focused fieldset": {
                                borderColor: "primary.main",
                              },
                            },
                            "& input": {
                              padding: "8px 12px",
                              fontSize: "14px",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Box mt="24px">
                      <Typography
                        variant="subtitle2"
                        mb={1}
                        sx={{ fontFamily: "Rethink Sans" }}
                      >
                        Create Password
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="****************"
                        name="new-password"
                        autoComplete="new-password"
                        type={showSignupPassword ? "text" : "password"}
                        value={signupData.password}
                        onChange={handleInputChange("password")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "40px",
                            borderRadius: "8px",
                            "& fieldset": { borderColor: "#E0E0E0" },
                            "&.Mui-focused fieldset": {
                              borderColor: "primary.main",
                            },
                          },
                          "& input": { padding: "8px 12px", fontSize: "14px" },
                        }}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ display: "flex", gap: 1 }}
                              >
                                <Tooltip
                                  title={
                                    <Box sx={{ p: 1 }}>
                                      <Stack spacing={0.5}>
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          spacing={1}
                                        >
                                          {passwordReqs.hasMinLength ? (
                                            <Check
                                              sx={{
                                                color: "green",
                                                fontSize: 16,
                                              }}
                                            />
                                          ) : (
                                            <Close
                                              sx={{
                                                color: "red",
                                                fontSize: 16,
                                              }}
                                            />
                                          )}
                                          <Typography
                                            sx={{ fontFamily: "Rethink Sans" }}
                                            fontSize={12}
                                            color="#0D1321"
                                          >
                                            At least 8 characters
                                          </Typography>
                                        </Stack>

                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          spacing={1}
                                        >
                                          {passwordReqs.hasCapital ? (
                                            <Check
                                              sx={{
                                                color: "green",
                                                fontSize: 16,
                                              }}
                                            />
                                          ) : (
                                            <Close
                                              sx={{
                                                color: "red",
                                                fontSize: 16,
                                              }}
                                            />
                                          )}
                                          <Typography
                                            sx={{ fontFamily: "Rethink Sans" }}
                                            fontSize={12}
                                            color="#0D1321"
                                          >
                                            At least 1 uppercase letter
                                          </Typography>
                                        </Stack>

                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          spacing={1}
                                        >
                                          {passwordReqs.hasNumber ? (
                                            <Check
                                              sx={{
                                                color: "green",
                                                fontSize: 16,
                                              }}
                                            />
                                          ) : (
                                            <Close
                                              sx={{
                                                color: "red",
                                                fontSize: 16,
                                              }}
                                            />
                                          )}
                                          <Typography
                                            sx={{ fontFamily: "Rethink Sans" }}
                                            fontSize={12}
                                            color="#0D1321"
                                          >
                                            At least 1 number
                                          </Typography>
                                        </Stack>

                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          spacing={1}
                                        >
                                          {passwordReqs.hasSpecialChar ? (
                                            <Check
                                              sx={{
                                                color: "green",
                                                fontSize: 16,
                                              }}
                                            />
                                          ) : (
                                            <Close
                                              sx={{
                                                color: "red",
                                                fontSize: 16,
                                              }}
                                            />
                                          )}
                                          <Typography
                                            sx={{ fontFamily: "Rethink Sans" }}
                                            fontSize={12}
                                            color="#0D1321"
                                          >
                                            At least 1 special character
                                          </Typography>
                                        </Stack>

                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          spacing={1}
                                        >
                                          {passwordReqs.passwordsMatch ? (
                                            <Check
                                              sx={{
                                                color: "green",
                                                fontSize: 16,
                                              }}
                                            />
                                          ) : (
                                            <Close
                                              sx={{
                                                color: "red",
                                                fontSize: 16,
                                              }}
                                            />
                                          )}
                                          <Typography
                                            sx={{ fontFamily: "Rethink Sans" }}
                                            fontSize={12}
                                            color="#0D1321"
                                          >
                                            Passwords match
                                          </Typography>
                                        </Stack>
                                      </Stack>
                                    </Box>
                                  }
                                  placement="top"
                                  arrow
                                  enterTouchDelay={0}
                                  leaveTouchDelay={3000}
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: "white",
                                        borderRadius: "8px",
                                        border: "1px solid #D1D5DB",
                                        color: "#0D1321",
                                        boxShadow: 2,
                                      },
                                    },
                                    arrow: {
                                      sx: {
                                        color: "#D1D5DB",
                                      },
                                    },
                                  }}
                                >
                                  <IconButton
                                    edge="end"
                                    aria-label="password info"
                                  >
                                    <InfoOutlined sx={{ fontSize: 20 }} />
                                  </IconButton>
                                </Tooltip>
                                <IconButton
                                  onClick={() =>
                                    setShowSignupPassword((v) => !v)
                                  }
                                  edge="end"
                                  aria-label="toggle password"
                                >
                                  {showSignupPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Box>

                    <Box mt="24px">
                      <Typography
                        variant="subtitle2"
                        mb={1}
                        sx={{ fontFamily: "Rethink Sans" }}
                      >
                        Confirm Password
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="****************"
                        name="new-password-confirm"
                        autoComplete="new-password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={signupData.confirmPassword}
                        onChange={handleInputChange("confirmPassword")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "40px",
                            borderRadius: "8px",
                            "& fieldset": { borderColor: "#E0E0E0" },
                            "&.Mui-focused fieldset": {
                              borderColor: "primary.main",
                            },
                          },
                          "& input": { padding: "8px 12px", fontSize: "14px" },
                        }}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    setShowConfirmPassword((v) => !v)
                                  }
                                  edge="end"
                                  aria-label="toggle confirm password"
                                >
                                  {showConfirmPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    sx={{
                      fontFamily: "Rethink Sans",
                      mt: 3,
                      borderRadius: "8px",
                      py: 1.3,
                      background:
                        "linear-gradient(90deg, #4F6CF3 0%, #617BFF 100%)",
                      textTransform: "none",
                      fontSize: 15,
                      fontWeight: 500,
                    }}
                    disabled={signupLoading}
                  >
                    {signupLoading ? "Creating..." : "Create Account"}
                  </Button>

                  <Typography
                    fontSize={12}
                    textAlign="center"
                    color="#6B7280"
                    mt={2}
                    sx={{ fontFamily: "Rethink Sans" }}
                  >
                    By signing up, I have read and agree to{" "}
                  </Typography>
                  <Typography
                    fontSize={12}
                    textAlign="center"
                    color="#6B7280"
                    sx={{ fontFamily: "Rethink Sans" }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color: "#4F6CF3",
                        cursor: "pointer",
                        fontFamily: "Rethink Sans",
                      }}
                      onClick={() =>
                        window.open(
                          "https://morelucro.com//policies/terms-and-conditions/",
                          "_blank"
                        )
                      }
                    >
                      Terms
                    </Box>{" "}
                    and{" "}
                    <Box
                      component="span"
                      sx={{
                        color: "#4F6CF3",
                        cursor: "pointer",
                        fontFamily: "Rethink Sans",
                      }}
                      onClick={() =>
                        window.open(
                          "https://morelucro.com/policies/privacy-policy/",
                          "_blank"
                        )
                      }
                    >
                      Privacy Policy
                    </Box>
                  </Typography>
                </Box>
              )}

              {/* Login */}
              {tab === "login" && (
                <Box component="form" onSubmit={handleLogin}>
                  <Box sx={{ width: "100%" }}>
                    <Box mt={2}>
                      <Typography
                        variant="subtitle2"
                        mb={1}
                        sx={{ fontFamily: "Rethink Sans" }}
                      >
                        Email Address
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="example@gmail.com"
                        value={loginData.email}
                        onBlur={handleEmailBlur("email", true)}
                        onKeyDown={handleEmailKeyDown("email", true)}
                        onChange={handleLoginInputChange("email")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "40px",
                            borderRadius: "8px",
                            "& fieldset": { borderColor: "#E0E0E0" },
                            "&.Mui-focused fieldset": {
                              borderColor: "primary.main",
                            },
                          },
                          "& input": { padding: "8px 12px", fontSize: "14px" },
                        }}
                      />
                    </Box>

                    {/* Password */}
                    <Box mt={3}>
                      <Typography
                        variant="subtitle2"
                        mb={1}
                        sx={{ fontFamily: "Rethink Sans" }}
                      >
                        Password
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="****************"
                        type={loginData.showPassword ? "text" : "password"}
                        value={loginData.password}
                        onChange={handleLoginInputChange("password")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "40px",
                            borderRadius: "8px",
                            "& fieldset": { borderColor: "#E0E0E0" },
                            "&.Mui-focused fieldset": {
                              borderColor: "primary.main",
                            },
                          },
                          "& input": { padding: "8px 12px", fontSize: "14px" },
                        }}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  edge="end"
                                  aria-label="toggle login password"
                                  onClick={() =>
                                    setLoginData((prev) => ({
                                      ...prev,
                                      showPassword: !prev.showPassword,
                                    }))
                                  }
                                >
                                  {loginData.showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Box>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "center", sm: "center" }}
                      spacing={{ xs: 1, sm: 0 }}
                      sx={{ mt: 2 }}
                    >
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          checked={loginData.keepMeSignedIn}
                          onChange={(e) =>
                            setLoginData((prev) => ({
                              ...prev,
                              keepMeSignedIn: e.target.checked,
                            }))
                          }
                          size="small"
                        />
                        <Typography sx={{ fontFamily: "Rethink Sans" }}>
                          Keep me signed in
                        </Typography>
                      </Stack>

                      <Link
                        href="/forgotPassword"
                        underline="hover"
                        sx={{
                          mt: { xs: 1, sm: 0 },
                          fontFamily: "Rethink Sans",
                        }}
                      >
                        Forgot password?
                      </Link>
                    </Stack>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{
                        fontFamily: "Rethink Sans",
                        mt: 3,
                        borderRadius: "8px",
                        py: 1.3,
                        background:
                          "linear-gradient(90deg, #4F6CF3 0%, #617BFF 100%)",
                        textTransform: "none",
                        fontSize: 15,
                        fontWeight: 500,
                      }}
                      disabled={loginData.loginLoading}
                    >
                      {loginData.loginLoading ? "Logging in..." : "Log In"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Container>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default WelcomeForm;
